import {useMemo} from 'react';

import {FieldProps} from '@/components/document//meta-field';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentLocale} from '@/components/document/hooks/use-document-locale';
import {useFrozenValueWhile} from '@/components/document/hooks/use-frozen-value-while';
import {useInvalidSectionFields} from '@/components/document/hooks/use-invalid-section-fields';
import {useIsAnyGroupModalOpen} from '@/components/document/hooks/use-is-any-group-modal-open';
import Table from '@/components/table';
import {TableData} from '@/components/table/table';
import {useConfig} from '@/contexts/config';
import {DocumentUIField, ValueResolver, VisibilitySet} from '@/engine/types';
import {TableRow} from '@/engine/types/document-model/document-table';
import {createColumnResolver} from '@/utils/document/value';

export const TableField = ({field}: FieldProps<DocumentUIField>) => {
  const {
    model: {accessMap},
  } = useConfig();
  const {documentModel, valueResolver, visibilitySet} = useDocumentContext();
  const {_t, locale} = useDocumentLocale();
  const invalidFields = useInvalidSectionFields();
  const isAnyGroupModalOpen = useIsAnyGroupModalOpen();

  const columnResolver = useMemo(() => {
    return createColumnResolver(valueResolver, locale, documentModel, accessMap, visibilitySet);
  }, [locale, documentModel, accessMap, visibilitySet, valueResolver]);

  const rowErrors = useMemo((): boolean[] => {
    if (!field.table) return [];

    const validateRow = createRowValidator(valueResolver, invalidFields, visibilitySet, field.table.array);

    return field.table.rows.map((_, index) => validateRow(index));
  }, [field, valueResolver, invalidFields, visibilitySet]);

  // While a modal is open, rows keep the error state they had when it opened to avoid flickering behind the modal
  const displayedRowErrors = useFrozenValueWhile(rowErrors, isAnyGroupModalOpen);

  const tableData = useMemo((): TableData => {
    if (!field.table) {
      return {headers: [], body: []};
    }

    const hasActionButton = field.table.rows.some(row => row.actionButton);
    const localizedHeaders = field.table.headers.map(_t);
    const headers = hasActionButton ? [...localizedHeaders, ''] : localizedHeaders;

    return {
      headers,
      body: field.table.rows.map((row: TableRow, index: number) => ({
        columns: row.columns.map(column => columnResolver(column)),
        onClick: row.disabled || row.onClick.length === 0 ? undefined : () => row.onClick.forEach(action => action()),
        disabled: row.disabled,
        actionButton: row.actionButton,
        isError: displayedRowErrors[index] ?? false,
      })),
    };
  }, [field, _t, columnResolver, displayedRowErrors]);

  return <Table dataTestid={field.id} data={tableData} theme={field.table?.theme}></Table>;
};

const createRowValidator =
  (valueResolver: ValueResolver, invalidFields: Set<string>, visibilitySet: VisibilitySet, arrayId?: string) =>
  (rowIndex: number): boolean => {
    const groupId = `${arrayId}#${rowIndex}`;

    if (invalidFields.size === 0) return false;

    // Get the fields for this array row
    const fieldsStr = valueResolver({$ref: `_${groupId}[fields]`});

    if (!fieldsStr) return false;

    const fields = fieldsStr.split(',');

    // Check validity for each field in the row
    return fields.some(fieldId => visibilitySet.has(fieldId) && invalidFields.has(fieldId));
  };
